import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Dialog,
  Drawer,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';

import { AM, AM_TEXT } from '../assignmentMonitoringTokens';
import {
  firstDay,
  formatLong,
  formatWeekday,
  lastDay,
  monthName,
  nextMonth,
  requirementError,
} from '../assignmentMonitoring';
import type { AssignmentMonitoringOverview, Requirement, SettingsChange } from '../assignmentMonitoringApi';

export interface AssignmentMonitoringDrawerProps {
  open: boolean;
  overview: AssignmentMonitoringOverview;
  onClose: () => void;
  onChange: (change: SettingsChange) => Promise<string | null>;
}

type Confirm = 'discard' | 'turnOff' | null;

const SAVE_FAILED = 'That change couldn’t be saved right now. Try again in a moment.';

const INPUT_SX = {
  width: 96,
  padding: '8px 12px',
  border: `1px solid ${AM.grey100}`,
  borderRadius: '8px',
  fontFamily: AM.font,
  ...AM_TEXT.body1Semi,
  color: AM.ink,
  bgcolor: AM.surface,
  outline: 0,
  '&:focus': { borderColor: AM.primary, boxShadow: `0 0 0 1px ${AM.primary}` },
} as const;

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: '12px', ...AM_TEXT.body1 }}>
      <Typography component="span" sx={{ width: 96, flexShrink: 0, color: AM.muted, ...AM_TEXT.overline, lineHeight: '24px' }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ ...AM_TEXT.body1, color: AM.ink }}>
        {value}
      </Typography>
    </Box>
  );
}

function GhostAction({
  label,
  icon,
  red,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  red?: boolean;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '8px 16px',
        borderRadius: '8px',
        fontFamily: AM.font,
        ...AM_TEXT.body1Semi,
        color: red ? AM.red700 : AM.ink,
        '&:hover': { bgcolor: red ? AM.red50 : AM.actionHover },
      }}
    >
      {icon}
      {label}
    </ButtonBase>
  );
}

function ConfirmDialog({
  open,
  title,
  body,
  keepLabel,
  confirmLabel,
  onKeep,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  keepLabel: string;
  confirmLabel: string;
  onKeep: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onKeep}
      aria-labelledby="am-confirm-title"
      slotProps={{ paper: { role: 'alertdialog', sx: { width: 400, maxWidth: 'calc(100% - 32px)', borderRadius: '12px', p: '24px', m: 0, boxShadow: AM.elevationModal } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box>
          <Typography id="am-confirm-title" sx={{ ...AM_TEXT.h4, color: AM.ink }}>
            {title}
          </Typography>
          <Typography sx={{ ...AM_TEXT.body1, color: AM.muted, mt: '8px' }}>{body}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button
            onClick={onKeep}
            sx={{ padding: '8px 16px', borderRadius: '8px', color: AM.primary, ...AM_TEXT.body1Bold, textTransform: 'none', '&:hover': { bgcolor: AM.indigo50 } }}
          >
            {keepLabel}
          </Button>
          <Button
            onClick={onConfirm}
            sx={{ padding: '8px 24px', borderRadius: '8px', bgcolor: AM.red700, color: AM.inverted, ...AM_TEXT.body1Bold, textTransform: 'none', '&:hover': { bgcolor: AM.red800 } }}
          >
            {confirmLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

// The requirement drawer: the two numbers, the exclude setting, the fixed
// rows, and the switch actions. Changes take effect next month, so the row
// above the footer says when, and the footer's left action undoes a pending
// turn-on or turn-off in one click. The form mounts fresh on every open, so
// it always starts from the rule in force (or the one waiting to start).
export function AssignmentMonitoringDrawer({ open, overview, onClose, onChange }: AssignmentMonitoringDrawerProps) {
  const closeRef = useRef<() => void>(onClose);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => closeRef.current()}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': 'am-drawer-title',
        sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100%', display: 'flex', flexDirection: 'column', boxShadow: AM.elevationModal },
      }}
    >
      <DrawerForm overview={overview} onClose={onClose} onChange={onChange} closeRef={closeRef} />
    </Drawer>
  );
}

function DrawerForm({
  overview,
  onClose,
  onChange,
  closeRef,
}: Omit<AssignmentMonitoringDrawerProps, 'open'> & { closeRef: React.MutableRefObject<() => void> }) {
  const base = overview.drawerRequirement;
  const [assign, setAssign] = useState(String(base.assign));
  const [score, setScore] = useState(String(base.score));
  const [exclude, setExclude] = useState(base.excludeBenchmarkMonths);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const error = requirementError(assign, score);
  const dirty = assign !== String(base.assign) || score !== String(base.score) || exclude !== base.excludeBenchmarkMonths;
  const nextFirst = firstDay(nextMonth(overview.month));
  const scheduledOn = !overview.on && Boolean(overview.turnsOn);
  const stopping = overview.on && Boolean(overview.stopsAfter);
  const requirement = (): Requirement => ({ assign: Number(assign), score: Number(score), excludeBenchmarkMonths: exclude });

  const run = async (change: SettingsChange) => {
    setBusy(true);
    setSaveError(null);
    try {
      await onChange(change);
    } catch (err: unknown) {
      console.warn('assignment monitoring settings change failed', err);
      setSaveError(SAVE_FAILED);
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (dirty) {
      setConfirm('discard');
      return;
    }
    onClose();
  };
  useEffect(() => {
    closeRef.current = close;
  });

  let changeRow: { label: string; value: string };
  if (stopping) changeRow = { label: 'Stops', value: `After ${formatLong(lastDay(overview.stopsAfter!))}` };
  else if (scheduledOn) changeRow = { label: 'Starts', value: formatLong(firstDay(overview.turnsOn!)) };
  else if (!overview.on) changeRow = { label: 'Starts', value: formatLong(nextFirst) };
  else changeRow = { label: 'Changes', value: `Start the next month, on ${formatLong(nextFirst)}` };

  let left: React.ReactNode = null;
  if (stopping) left = <GhostAction label="Keep it on" icon={<UndoIcon sx={{ fontSize: 16 }} />} onClick={() => void run({ action: 'keepOn' })} />;
  else if (scheduledOn) left = <GhostAction label="Cancel turning on" icon={<UndoIcon sx={{ fontSize: 16 }} />} onClick={() => void run({ action: 'cancelTurnOn' })} />;
  else if (overview.on) left = <GhostAction label="Turn off" icon={<PowerSettingsNewIcon sx={{ fontSize: 16 }} />} red onClick={() => setConfirm('turnOff')} />;

  // While a turn-off is pending there is no next month to start a rule in:
  // Keep it on first, then edit.
  const primary =
    !overview.on && !scheduledOn
      ? { label: 'Turn on', disabled: Boolean(error) || busy, onClick: () => void run({ action: 'turnOn', requirement: requirement() }) }
      : { label: 'Save', disabled: Boolean(error) || !dirty || busy || stopping, onClick: () => void run({ action: 'save', requirement: requirement() }) };

  const skippedList = overview.skippedMonths
    .map((s) => `${monthName(s.month)} (${s.window.kind} window, ${formatWeekday(s.window.start)} – ${formatWeekday(s.window.close)})`)
    .join(', ');

  return (
    <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', p: '24px', borderBottom: `1px solid ${AM.divider}` }}>
          <Box>
            <Typography id="am-drawer-title" sx={{ ...AM_TEXT.h4, color: AM.ink }}>
              Assignment monitoring requirement
            </Typography>
            <Typography sx={{ ...AM_TEXT.body1, color: AM.muted, mt: '4px' }}>
              What every teacher&apos;s students must complete each month.
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={close} sx={{ width: 32, height: 32, m: '-4px -4px 0 0' }}>
            <CloseIcon sx={{ fontSize: 24, color: AM.muted }} />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography component="label" htmlFor="am-assign" sx={{ ...AM_TEXT.body1Semi, color: AM.ink }}>
              Assignments each student must receive
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: AM.muted, ...AM_TEXT.body1 }}>
              <Box component="input" id="am-assign" inputMode="numeric" value={assign} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAssign(e.target.value.trim())} sx={INPUT_SX} />
              per month
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography component="label" htmlFor="am-score" sx={{ ...AM_TEXT.body1Semi, color: AM.ink }}>
              How many of those must be scored
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: AM.muted, ...AM_TEXT.body1 }}>
              <Box
                component="input"
                id="am-score"
                inputMode="numeric"
                value={score}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScore(e.target.value.trim())}
                sx={{
                  ...INPUT_SX,
                  ...(error && Number(score) > Number(assign)
                    ? { borderColor: AM.red700, boxShadow: `0 0 0 1px ${AM.red700}` }
                    : {}),
                }}
              />
              per month
            </Box>
            {error ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: AM.red700, ...AM_TEXT.body2Bold }}>
                <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                <span>{error}</span>
              </Box>
            ) : null}
            <Typography sx={{ ...AM_TEXT.body2, color: AM.muted }}>
              Scored means a teacher reviewed and approved the score. Approved AI scores count.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <FormControlLabel
              sx={{ m: 0, gap: '12px', '& .MuiTypography-root': { ...AM_TEXT.body1, color: AM.ink } }}
              control={<Checkbox checked={exclude} onChange={(e) => setExclude(e.target.checked)} sx={{ p: 0, color: AM.actionActive, '&.Mui-checked': { color: AM.primary } }} />}
              label="Exclude months with a benchmark"
            />
            <Typography sx={{ pl: '32px', ...AM_TEXT.body2, color: AM.muted }}>
              Skips a month only when a benchmark window covers every day of it.
            </Typography>
            {exclude ? (
              <Typography sx={{ pl: '32px', ...AM_TEXT.body2Bold, color: AM.ink }}>
                This school year: {skippedList || 'none'}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', p: '16px', borderRadius: '8px', bgcolor: AM.background }}>
            <SummaryRow label="Period" value="Every calendar month" />
            <SummaryRow label="Applies to" value="Every student of every teacher using Flashlight360" />
            <SummaryRow label={changeRow.label} value={changeRow.value} />
          </Box>
        </Box>
        {saveError ? (
          <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: '4px', p: '0 24px 16px', color: AM.red700, ...AM_TEXT.body2Bold }}>
            <ErrorOutlineIcon sx={{ fontSize: 14 }} />
            <span>{saveError}</span>
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '16px 24px', borderTop: `1px solid ${AM.divider}` }}>
          <Box sx={{ flex: 1 }}>{left}</Box>
          <Button
            onClick={close}
            sx={{ padding: '8px 16px', borderRadius: '8px', color: AM.primary, ...AM_TEXT.body1Bold, textTransform: 'none', '&:hover': { bgcolor: AM.indigo50 } }}
          >
            Cancel
          </Button>
          <Button
            disabled={primary.disabled}
            onClick={primary.onClick}
            sx={{
              padding: '8px 24px',
              borderRadius: '8px',
              bgcolor: AM.primary,
              color: AM.primaryContrast,
              ...AM_TEXT.body1Bold,
              textTransform: 'none',
              '&:hover': { bgcolor: AM.indigo600 },
              '&.Mui-disabled': { bgcolor: AM.actionDisabledBg, color: AM.actionDisabled },
            }}
          >
            {primary.label}
          </Button>
        </Box>
      <ConfirmDialog
        open={confirm === 'discard'}
        title="Discard changes?"
        body="Your changes to the requirement won’t be saved. The current requirement stays in place."
        keepLabel="Keep editing"
        confirmLabel="Discard"
        onKeep={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          onClose();
        }}
      />
      <ConfirmDialog
        open={confirm === 'turnOff'}
        title="Turn off assignment monitoring?"
        body={`It stops at the end of ${monthName(overview.month)}. Past records stay available.`}
        keepLabel="Keep it on"
        confirmLabel="Turn off"
        onKeep={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void run({ action: 'turnOff' });
        }}
      />
    </>
  );
}
